import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, DatePicker, Divider, Input, Message, Modal, Panel, SelectPicker, Stack, Tag, Tooltip, Whisper, toaster } from 'rsuite';

import {
  useCloseDates,
  useCreateMilestone,
  useCreateTaskOrder,
  useDeleteMilestone,
  useDeleteProjectPhoto,
  useDeleteProject,
  useProjectMilestones,
  useProjects,
  useProjectUsers,
  useSetProjectUsers,
  useTaskOrders,
  useUploadProjectPhoto,
  useUpdateMilestone,
  useUpdateProject,
  useUpdateTaskOrder,
  useUsers,
  useUsersWorkplanForMonths,
} from '../hooks/useQueries';

import { clearRsuitePickerSearchInputSoon } from '../utils/rsuitePickerSearch';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return currencyFormatter.format(n);
};

const digitsOnly = (raw) => String(raw ?? '').replace(/[^0-9]/g, '');

const parseMoney = (raw) => {
  const s = String(raw ?? '').replace(/[$,\s]/g, '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const normalizeDate10 = (raw) => {
  if (raw instanceof Date) {
    const t = raw.getTime();
    if (Number.isNaN(t)) return null;
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, '0');
    const d = String(raw.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = typeof raw === 'string' ? raw.slice(0, 10) : '';
  return s ? s : null;
};

const isoDateToDate = (raw) => {
  const iso = normalizeDate10(raw);
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const out = new Date(y, m - 1, d);
  out.setHours(0, 0, 0, 0);
  return Number.isNaN(out.getTime()) ? null : out;
};

const initialsForName = (raw) => {
  const parts = String(raw || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
  const s = `${first}${last}`.toUpperCase();
  return s || '—';
};

const avatarStyleForId = (id) => {
  const n = Number(id);
  const safe = Number.isFinite(n) ? Math.abs(Math.trunc(n)) : 0;
  const hue = (safe * 137.508) % 360;
  const background = `hsl(${hue} 70% 90%)`;
  const borderColor = `hsl(${hue} 55% 78%)`;
  return { background, borderColor };
};

const isIsoDate = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

const toMonthStartIso = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const monthRangeInclusive = (startIso, endIso) => {
  if (!isIsoDate(startIso) || !isIsoDate(endIso)) return [];
  const start = new Date(Number(startIso.slice(0, 4)), Number(startIso.slice(5, 7)) - 1, 1);
  const end = new Date(Number(endIso.slice(0, 4)), Number(endIso.slice(5, 7)) - 1, 1);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start.getTime() > end.getTime()) return [];

  const out = [];
  const cur = new Date(start);
  while (cur.getTime() <= end.getTime()) {
    out.push(toMonthStartIso(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return out;
};

const countWeekdaysInclusive = (start, endExclusive) => {
  if (!(start instanceof Date) || !(endExclusive instanceof Date)) return 0;
  const a = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const b = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  if (a.getTime() >= b.getTime()) return 0;

  let count = 0;
  const cur = new Date(a);
  while (cur.getTime() < b.getTime()) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

const parseMonthDay = (raw) => {
  const s = String(raw ?? '').slice(0, 10);
  const m = s.match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { month: Number(m[1]), day: Number(m[2]) };
};

const countCloseDatesInRange = (start, endExclusive, rows) => {
  if (!(start instanceof Date) || !(endExclusive instanceof Date)) return 0;
  const list = Array.isArray(rows) ? rows : [];

  const a = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const b = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  if (a.getTime() >= b.getTime()) return 0;

  const years = new Set();
  for (let y = a.getFullYear(); y <= b.getFullYear(); y += 1) years.add(y);

  let count = 0;
  for (const r of list) {
    const md = parseMonthDay(r?.closeDate ?? r?.close_date);
    if (!md || !Number.isFinite(md.month) || !Number.isFinite(md.day)) continue;

    for (const y of years) {
      const d = new Date(y, md.month - 1, md.day);
      d.setHours(0, 0, 0, 0);
      if (d.getTime() >= a.getTime() && d.getTime() < b.getTime()) {
        count += 1;
      }
    }
  }
  return count;
};

const sectionHeaderStyle = {
  textAlign: 'center',
  fontWeight: 700,
};

export default function ProjectEditPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const projectId = Number(params?.projectId);

  const {
    data: projects = [],
    isLoading: projectsLoading,
    error: projectsError,
  } = useProjects();

  const {
    data: users = [],
    isLoading: usersLoading,
    error: usersError,
  } = useUsers();

  const { data: closeDates = [] } = useCloseDates();

  const {
    data: projectUsersData,
    isLoading: projectUsersLoading,
    error: projectUsersError,
  } = useProjectUsers(projectId, { enabled: Number.isInteger(projectId) });

  const projectUsers = useMemo(() => (Array.isArray(projectUsersData) ? projectUsersData : []), [projectUsersData]);

  const project = useMemo(() => {
    if (!Number.isInteger(projectId)) return null;
    return (projects || []).find((p) => Number(p?.id) === projectId) || null;
  }, [projects, projectId]);

  const projectStartIso = useMemo(() => normalizeDate10(project?.startDate ?? project?.start_date), [project]);
  const projectEndIso = useMemo(() => normalizeDate10(project?.endDate ?? project?.end_date), [project]);

  const [name, setName] = useState('');
  const [picUserId, setPicUserId] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [budgetTarget, setBudgetTarget] = useState('');
  const [contractNumber, setContractNumber] = useState('');

  useEffect(() => {
    if (!project) return;

    setName(String(project?.name ?? ''));
    setPicUserId(Number.isInteger(Number(project?.picUserId)) ? Number(project.picUserId) : null);
    setStartDate(normalizeDate10(project?.startDate) ?? '');
    setEndDate(normalizeDate10(project?.endDate) ?? '');
    setBudgetTarget(
      project?.budgetTarget === null || project?.budgetTarget === undefined ? '' : formatCurrency(project.budgetTarget)
    );
    setContractNumber(String(project?.contractNumber ?? project?.contract_number ?? ''));
  }, [project]);

  const updateProject = useUpdateProject({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Project saved.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const deleteProject = useDeleteProject({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Project deleted.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const uploadProjectPhoto = useUploadProjectPhoto({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Project photo uploaded.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const deleteProjectPhoto = useDeleteProjectPhoto({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Project photo removed.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [milestoneModalOpen, setMilestoneModalOpen] = useState(false);
  const [milestoneModalMode, setMilestoneModalMode] = useState('create');
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [milestoneNameInput, setMilestoneNameInput] = useState('');
  const [milestoneDateInput, setMilestoneDateInput] = useState(null);
  const [pendingDeleteMilestone, setPendingDeleteMilestone] = useState(null);

  const canSave = useMemo(() => {
    if (!Number.isInteger(projectId)) return false;
    if (!String(name || '').trim()) return false;
    if (updateProject.isPending) return false;
    return true;
  }, [name, projectId, updateProject.isPending]);

  const canDelete = useMemo(() => {
    if (!Number.isInteger(projectId)) return false;
    if (deleteProject.isPending) return false;
    if (updateProject.isPending) return false;
    return true;
  }, [projectId, deleteProject.isPending, updateProject.isPending]);

  const {
    data: milestonesData,
    isLoading: milestonesLoading,
    error: milestonesError,
  } = useProjectMilestones(projectId, { enabled: Number.isInteger(projectId) });

  const createMilestone = useCreateMilestone();
  const deleteMilestone = useDeleteMilestone();
  const updateMilestone = useUpdateMilestone();

  const milestones = useMemo(() => {
    const rows = Array.isArray(milestonesData) ? milestonesData : [];
    return [...rows].sort((a, b) => {
      const ad = String(a?.milestone_date || '');
      const bd = String(b?.milestone_date || '');
      if (ad !== bd) return ad.localeCompare(bd);
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
  }, [milestonesData]);

  const openCreateMilestoneModal = () => {
    setMilestoneModalMode('create');
    setEditingMilestone(null);
    setMilestoneNameInput('');
    setMilestoneDateInput(null);
    setMilestoneModalOpen(true);
  };

  const openEditMilestoneModal = (milestone) => {
    if (!milestone || milestone.is_auto) return;
    setMilestoneModalMode('edit');
    setEditingMilestone(milestone);
    setMilestoneNameInput(String(milestone?.name || ''));
    setMilestoneDateInput(isoDateToDate(milestone?.milestone_date));
    setMilestoneModalOpen(true);
  };

  const closeMilestoneModal = () => {
    setMilestoneModalOpen(false);
    setEditingMilestone(null);
    setMilestoneNameInput('');
    setMilestoneDateInput(null);
  };

  const milestoneFormValid = useMemo(() => {
    const name = String(milestoneNameInput || '').trim();
    if (!name) return false;
    const iso = normalizeDate10(milestoneDateInput);
    return Boolean(iso && /^\d{4}-\d{2}-\d{2}$/.test(iso));
  }, [milestoneNameInput, milestoneDateInput]);

  const saveMilestone = async () => {
    if (!Number.isInteger(projectId) || !milestoneFormValid) return;
    const name = String(milestoneNameInput || '').trim();
    const milestone_date = normalizeDate10(milestoneDateInput);

    try {
      if (milestoneModalMode === 'edit') {
        const mid = Number(editingMilestone?.id);
        if (!Number.isInteger(mid)) return;
        await updateMilestone.mutateAsync({
          projectId,
          milestoneId: mid,
          payload: { name, milestone_date },
        });
      } else {
        await createMilestone.mutateAsync({
          projectId,
          payload: { name, milestone_date },
        });
      }
      closeMilestoneModal();
    } catch (err) {
      toaster.push(
        <Message showIcon type="error" closable>
          Failed to save milestone: {String(err?.message || err)}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const confirmDeleteMilestone = async () => {
    if (!Number.isInteger(projectId) || !pendingDeleteMilestone) return;
    const milestoneId = pendingDeleteMilestone?.id;
    if (milestoneId === null || milestoneId === undefined || milestoneId === '') return;

    try {
      await deleteMilestone.mutateAsync({ projectId, milestoneId });
      setPendingDeleteMilestone(null);
    } catch (err) {
      toaster.push(
        <Message showIcon type="error" closable>
          Failed to delete milestone: {String(err?.message || err)}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const onSave = async () => {
    if (!Number.isInteger(projectId)) return;

    const payload = {
      name: String(name || '').trim(),
      picUserId: Number.isInteger(Number(picUserId)) ? Number(picUserId) : null,
      startDate: normalizeDate10(startDate),
      endDate: normalizeDate10(endDate),
      budgetTarget: parseMoney(budgetTarget),
      contractNumber: String(contractNumber || '').trim() || null,
    };

    await updateProject.mutateAsync({ projectId, payload });

    navigate(`/projects/${projectId}`, { replace: true });
  };

  const onDelete = async () => {
    if (!Number.isInteger(projectId)) return;
    await deleteProject.mutateAsync(projectId);
    navigate('/', { replace: true });
  };

  const onSelectProjectPhoto = async (event) => {
    if (!Number.isInteger(projectId)) return;
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!String(file.type || '').startsWith('image/')) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select an image file.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Image is too large (max 5MB).
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const toBase64 = () =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const full = String(reader.result || '');
          const commaIdx = full.indexOf(',');
          const b64 = commaIdx >= 0 ? full.slice(commaIdx + 1) : full;
          resolve(b64);
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

    try {
      const dataBase64 = await toBase64();
      await uploadProjectPhoto.mutateAsync({
        projectId,
        payload: {
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          dataBase64,
        },
      });
    } catch (err) {
      toaster.push(
        <Message showIcon type="error" closable>
          Failed to upload photo: {String(err?.message || err)}
        </Message>,
        { placement: 'topEnd' }
      );
    } finally {
      if (event?.target) event.target.value = '';
    }
  };

  const goBack = () => {
    const from = location?.state?.from;
    if (typeof from === 'string' && from.startsWith('/')) {
      navigate(from, { replace: true });
      return;
    }
    navigate(`/projects/${projectId}`, { replace: true });
  };

  const {
    data: taskOrdersData,
    isLoading: taskOrdersLoading,
    error: taskOrdersError,
  } = useTaskOrders(projectId, { enabled: Number.isInteger(projectId) });

  const taskOrders = useMemo(() => (Array.isArray(taskOrdersData) ? taskOrdersData : []), [taskOrdersData]);
  const explicitTaskOrders = useMemo(() => {
    const rows = (taskOrders || []).filter((o) => !o?.isDefault);

    const dayFromIso = (iso) => {
      const s = String(iso || '').slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
      const d = new Date(`${s}T00:00:00`);
      const t = d.getTime();
      return Number.isNaN(t) ? null : Math.floor(t / 86400000);
    };

    return rows.slice().sort((a, b) => {
      const aStart = dayFromIso(a?.startDate ?? a?.start_date);
      const bStart = dayFromIso(b?.startDate ?? b?.start_date);
      if (aStart !== null && bStart !== null && aStart !== bStart) return bStart - aStart;
      if (aStart !== null && bStart === null) return -1;
      if (aStart === null && bStart !== null) return 1;

      const aEnd = dayFromIso(a?.endDate ?? a?.end_date);
      const bEnd = dayFromIso(b?.endDate ?? b?.end_date);
      if (aEnd !== null && bEnd !== null && aEnd !== bEnd) return bEnd - aEnd;
      if (aEnd !== null && bEnd === null) return -1;
      if (aEnd === null && bEnd !== null) return 1;

      const aSort = Number.isFinite(Number(a?.sortOrder ?? a?.sort_order)) ? Number(a?.sortOrder ?? a?.sort_order) : null;
      const bSort = Number.isFinite(Number(b?.sortOrder ?? b?.sort_order)) ? Number(b?.sortOrder ?? b?.sort_order) : null;
      if (aSort !== null && bSort !== null && aSort !== bSort) return bSort - aSort;
      if (aSort !== null && bSort === null) return -1;
      if (aSort === null && bSort !== null) return 1;

      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
  }, [taskOrders]);
  const hasExplicitTaskOrders = explicitTaskOrders.length > 0;
  const hasOnlyProjectTaskOrder = useMemo(
    () =>
      explicitTaskOrders.length === 1
      && String(explicitTaskOrders?.[0]?.name || '').trim().toLowerCase() === 'project',
    [explicitTaskOrders]
  );
  const defaultTaskOrder = useMemo(
    () => (taskOrders || []).find((o) => String(o?.name ?? '').toLowerCase() === 'default') ?? null,
    [taskOrders]
  );

  const updateTaskOrder = useUpdateTaskOrder({
    onSuccess: async (_data, variables) => {
      const hasDateChanges =
        Object.prototype.hasOwnProperty.call(variables?.payload || {}, 'startDate')
        || Object.prototype.hasOwnProperty.call(variables?.payload || {}, 'endDate');

      toaster.push(
        <Message showIcon type="success" closable>
          {hasDateChanges ? 'Task order updated. Auto milestones recalculated.' : 'Task order updated.'}
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const createTaskOrder = useCreateTaskOrder({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Task order created.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const setProjectUsers = useSetProjectUsers({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Project people updated.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const assignedUserIds = useMemo(
    () => (projectUsers || []).map((u) => Number(u?.id)).filter((id) => Number.isInteger(id)),
    [projectUsers]
  );

  const [addPersonUserId, setAddPersonUserId] = useState(null);
  const addPersonPickerRef = useRef(null);
  const [removePerson, setRemovePerson] = useState(null);

  const availableUserOptions = useMemo(() => {
    const assigned = new Set(assignedUserIds);
    return (users || [])
      .map((u) => ({ label: u?.name || '—', value: Number(u?.id) }))
      .filter((o) => Number.isInteger(o.value) && !assigned.has(o.value));
  }, [users, assignedUserIds]);

  const projectPeriodMonths = useMemo(() => {
    const startIso = normalizeDate10(startDate) || projectStartIso;
    const endIso = normalizeDate10(endDate) || projectEndIso;
    return monthRangeInclusive(startIso, endIso);
  }, [startDate, endDate, projectStartIso, projectEndIso]);

  const availableUserIds = useMemo(
    () => availableUserOptions.map((o) => Number(o.value)).filter((id) => Number.isInteger(id)),
    [availableUserOptions]
  );

  const workplanQueries = useUsersWorkplanForMonths(availableUserIds, projectPeriodMonths, {
    enabled: availableUserIds.length > 0 && projectPeriodMonths.length > 0,
  });

  const userFteById = useMemo(() => {
    const map = new Map();
    for (const u of users || []) {
      const id = Number(u?.id);
      if (!Number.isInteger(id)) continue;
      const fteRaw = Number(u?.ftePercent ?? u?.fte_percent ?? 100);
      const fte = Number.isFinite(fteRaw) ? Math.max(0, fteRaw) : 100;
      map.set(id, fte / 100);
    }
    return map;
  }, [users]);

  const workableHoursByMonth = useMemo(() => {
    const out = {};
    for (const ms of projectPeriodMonths) {
      if (!isIsoDate(ms)) {
        out[ms] = 0;
        continue;
      }
      const y = Number(ms.slice(0, 4));
      const m0 = Number(ms.slice(5, 7)) - 1;
      const start = new Date(y, m0, 1);
      start.setHours(0, 0, 0, 0);
      const endExclusive = new Date(y, m0 + 1, 1);
      endExclusive.setHours(0, 0, 0, 0);
      const weekdays = countWeekdaysInclusive(start, endExclusive);
      const closeCount = countCloseDatesInRange(start, endExclusive, closeDates);
      out[ms] = Math.max(0, weekdays * 8 - closeCount * 8);
    }
    return out;
  }, [projectPeriodMonths, closeDates]);

  const avgUtilizationByUserId = useMemo(() => {
    const map = new Map();
    if (projectPeriodMonths.length === 0) return map;

    availableUserIds.forEach((userId, idx) => {
      const q = workplanQueries?.[idx];
      const rows = Array.isArray(q?.data?.rows) ? q.data.rows : [];
      const fteScale = Number(userFteById.get(userId) ?? 1);

      let monthSum = 0;
      let monthCount = 0;

      for (const ms of projectPeriodMonths) {
        const hours = rows.reduce((sum, r) => sum + Number(r?.hours?.[ms] ?? 0), 0);
        const workable = Number(workableHoursByMonth?.[ms] ?? 0) * fteScale;
        const util = workable > 0 ? (hours / workable) * 100 : 0;
        monthSum += Number.isFinite(util) ? util : 0;
        monthCount += 1;
      }

      map.set(userId, monthCount > 0 ? monthSum / monthCount : 0);
    });

    return map;
  }, [availableUserIds, projectPeriodMonths, workplanQueries, userFteById, workableHoursByMonth]);

  const availableUserOptionsWithUtil = useMemo(() => {
    return availableUserOptions.map((o) => {
      const avg = Number(avgUtilizationByUserId.get(Number(o.value)) ?? 0);
      const rounded = Number.isFinite(avg) ? Math.round(avg) : 0;
      return {
        ...o,
        utilizationLabel: `${rounded}% Average utilization over project duration`,
        utilizationValue: rounded,
      };
    });
  }, [availableUserOptions, avgUtilizationByUserId]);

  const addPerson = async (userId) => {
    if (!Number.isInteger(projectId)) return;
    const id = Number(userId);
    if (!Number.isInteger(id)) return;
    if (assignedUserIds.includes(id)) return;

    await setProjectUsers.mutateAsync({ projectId, userIds: [...assignedUserIds, id] });
  };

  const removePersonFromProject = async (userId) => {
    if (!Number.isInteger(projectId)) return;
    const id = Number(userId);
    if (!Number.isInteger(id)) return;

    const next = assignedUserIds.filter((x) => x !== id);
    await setProjectUsers.mutateAsync({ projectId, userIds: next });
  };

  const [editTaskOrderOpen, setEditTaskOrderOpen] = useState(false);
  const [editingTaskOrderId, setEditingTaskOrderId] = useState(null);
  const [taskOrderName, setTaskOrderName] = useState('');
  const [taskOrderStartDate, setTaskOrderStartDate] = useState('');
  const [taskOrderEndDate, setTaskOrderEndDate] = useState('');
  const [originalTaskOrderStartDate, setOriginalTaskOrderStartDate] = useState('');
  const [originalTaskOrderEndDate, setOriginalTaskOrderEndDate] = useState('');
  const [taskOrderBudgetTarget, setTaskOrderBudgetTarget] = useState('');

  const openEditTaskOrder = (taskOrder) => {
    const id = Number(taskOrder?.id);
    if (!Number.isInteger(id)) return;
    if (taskOrder?.isDefault) return;

    setEditingTaskOrderId(id);
    setTaskOrderName(String(taskOrder?.name ?? ''));
    const startIso = normalizeDate10(taskOrder?.startDate ?? taskOrder?.start_date) ?? '';
    const endIso = normalizeDate10(taskOrder?.endDate ?? taskOrder?.end_date) ?? '';
    setTaskOrderStartDate(startIso);
    setTaskOrderEndDate(endIso);
    setOriginalTaskOrderStartDate(startIso);
    setOriginalTaskOrderEndDate(endIso);
    setTaskOrderBudgetTarget(
      taskOrder?.budgetTarget === null || taskOrder?.budgetTarget === undefined
        ? ''
        : formatCurrency(taskOrder.budgetTarget)
    );
    setEditTaskOrderOpen(true);
  };

  const canSaveTaskOrder = useMemo(() => {
    const id = Number(editingTaskOrderId);
    if (!Number.isInteger(id)) return false;
    if (!String(taskOrderName || '').trim()) return false;

    const s = normalizeDate10(taskOrderStartDate);
    const e = normalizeDate10(taskOrderEndDate);
    if (!s || !e) return false;
    if (String(e).localeCompare(String(s)) < 0) return false;

    const originalS = normalizeDate10(originalTaskOrderStartDate);
    const originalE = normalizeDate10(originalTaskOrderEndDate);
    const startChanged = String(s) !== String(originalS || '');
    const endChanged = String(e) !== String(originalE || '');

    if (startChanged && projectStartIso && String(s).localeCompare(String(projectStartIso)) < 0) return false;
    if (endChanged && projectEndIso && String(e).localeCompare(String(projectEndIso)) > 0) return false;

    if (updateTaskOrder.isPending) return false;
    return true;
  }, [
    editingTaskOrderId,
    taskOrderName,
    taskOrderStartDate,
    taskOrderEndDate,
    originalTaskOrderStartDate,
    originalTaskOrderEndDate,
    projectStartIso,
    projectEndIso,
    updateTaskOrder.isPending,
  ]);

  const saveTaskOrderEdit = async () => {
    const id = Number(editingTaskOrderId);
    if (!Number.isInteger(id)) return;

    const payload = {
      name: String(taskOrderName || '').trim(),
      startDate: normalizeDate10(taskOrderStartDate),
      endDate: normalizeDate10(taskOrderEndDate),
      budgetTarget: parseMoney(taskOrderBudgetTarget),
    };

    await updateTaskOrder.mutateAsync({ projectId, taskOrderId: id, payload });
    setEditTaskOrderOpen(false);
  };

  const [addTaskOrderOpen, setAddTaskOrderOpen] = useState(false);
  const [newTaskOrderName, setNewTaskOrderName] = useState('');
  const [newTaskOrderStartDate, setNewTaskOrderStartDate] = useState('');
  const [newTaskOrderEndDate, setNewTaskOrderEndDate] = useState('');
  const [newTaskOrderBudgetTarget, setNewTaskOrderBudgetTarget] = useState('');

  const openAddTaskOrder = () => {
    setNewTaskOrderName('');
    setNewTaskOrderStartDate(projectStartIso ?? '');
    setNewTaskOrderEndDate(projectEndIso ?? '');
    setNewTaskOrderBudgetTarget('');
    setAddTaskOrderOpen(true);
  };

  const canCreateTaskOrder = useMemo(() => {
    if (!Number.isInteger(projectId)) return false;
    if (!String(newTaskOrderName || '').trim()) return false;

    const s = normalizeDate10(newTaskOrderStartDate);
    const e = normalizeDate10(newTaskOrderEndDate);
    if (!s || !e) return false;
    if (String(e).localeCompare(String(s)) < 0) return false;
    if (projectStartIso && String(s).localeCompare(String(projectStartIso)) < 0) return false;
    if (projectEndIso && String(e).localeCompare(String(projectEndIso)) > 0) return false;

    if (createTaskOrder.isPending) return false;
    return true;
  }, [
    projectId,
    newTaskOrderName,
    newTaskOrderStartDate,
    newTaskOrderEndDate,
    projectStartIso,
    projectEndIso,
    createTaskOrder.isPending,
  ]);

  const createNewTaskOrder = async () => {
    if (!Number.isInteger(projectId)) return;
    const payload = {
      name: String(newTaskOrderName || '').trim(),
      startDate: normalizeDate10(newTaskOrderStartDate),
      endDate: normalizeDate10(newTaskOrderEndDate),
      budgetTarget: parseMoney(newTaskOrderBudgetTarget),
    };

    await createTaskOrder.mutateAsync({ projectId, payload });
    setAddTaskOrderOpen(false);
  };

  const userOptions = useMemo(
    () => (users || []).map((u) => ({ label: u.name, value: Number(u.id) })).filter((u) => Number.isInteger(u.value)),
    [users]
  );

  const projectPeopleDisplay = useMemo(() => {
    return (projectUsers || [])
      .map((u) => ({ id: Number(u?.id), name: u?.name || '—' }))
      .filter((u) => Number.isInteger(u.id))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [projectUsers]);

  if (!Number.isInteger(projectId)) {
    return (
      <div className="ppc-page">
        <Message type="error" showIcon>
          Invalid project.
        </Message>
      </div>
    );
  }

  return (
    <div className="ppc-page">
      <Stack justifyContent="space-between" alignItems="center" wrap>
        <h3 style={{ margin: 0 }}>Edit Project</h3>
        <Button appearance="ghost" onClick={goBack}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Back
          </span>
        </Button>
      </Stack>

      {projectsLoading ? <div>Loading project…</div> : null}
      {projectsError ? (
        <Message type="error" showIcon>
          Failed to load projects.
        </Message>
      ) : null}

      {!projectsLoading && !project ? (
        <Message type="warning" showIcon>
          Project not found.
        </Message>
      ) : null}

      {project ? (
        <Panel
          bordered
          header={<div style={sectionHeaderStyle}>Project Info</div>}
          style={{ width: '100%', maxWidth: 1100, margin: '0 auto' }}
        >
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#666' }}>Project name</div>
                <Input value={name} onChange={setName} placeholder="e.g. ProPlan 2026" />
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#666' }}>PIC</div>
                {usersError ? (
                  <Message type="error" showIcon>
                    Failed to load users.
                  </Message>
                ) : null}
                <SelectPicker
                  cleanable
                  searchable
                  placeholder={usersLoading ? 'Loading users…' : 'Select a PIC (optional)'}
                  style={{ width: '100%' }}
                  data={userOptions}
                  value={Number.isInteger(Number(picUserId)) ? Number(picUserId) : null}
                  onChange={(v) => setPicUserId(Number.isInteger(v) ? v : null)}
                  onClean={() => setPicUserId(null)}
                  disabled={usersLoading || userOptions.length === 0 || updateProject.isPending}
                />
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6, textAlign: 'center' }}>Project Photo</div>
              <Stack spacing={10} alignItems="center" justifyContent="center" wrap style={{ width: '100%' }}>
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 8,
                    border: '1px solid #ddd',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fafafa',
                    flexShrink: 0,
                  }}
                >
                  {project?.photoUrl ? (
                    <img
                      src={project.photoUrl}
                      alt="Project"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span style={{ fontSize: 11, color: '#888' }}>No image</span>
                  )}
                </div>

                <label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      void onSelectProjectPhoto(e);
                    }}
                    style={{ display: 'none' }}
                    disabled={uploadProjectPhoto.isPending || deleteProjectPhoto.isPending}
                  />
                  <Button
                    as="span"
                    appearance="ghost"
                    size="sm"
                    loading={uploadProjectPhoto.isPending}
                    disabled={uploadProjectPhoto.isPending || deleteProjectPhoto.isPending}
                  >
                    Upload
                  </Button>
                </label>

                {project?.photoUrl ? (
                  <Button
                    appearance="ghost"
                    color="red"
                    size="sm"
                    loading={deleteProjectPhoto.isPending}
                    disabled={uploadProjectPhoto.isPending || deleteProjectPhoto.isPending}
                    onClick={async () => {
                      if (!Number.isInteger(projectId)) return;
                      try {
                        await deleteProjectPhoto.mutateAsync({ projectId });
                      } catch (err) {
                        toaster.push(
                          <Message showIcon type="error" closable>
                            Failed to remove photo: {String(err?.message || err)}
                          </Message>,
                          { placement: 'topEnd' }
                        );
                      }
                    }}
                  >
                    Remove
                  </Button>
                ) : null}
              </Stack>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4, textAlign: 'center' }}>PNG/JPG/WebP up to 5MB.</div>
            </div>

            <Stack spacing={12} wrap>
              <div style={{ flex: '1 1 220px', minWidth: 220 }}>
                <div style={{ fontSize: 12, color: '#666' }}>Start date</div>
                <DatePicker
                  oneTap
                  cleanable
                  format="MM/dd/yyyy"
                  value={isoDateToDate(startDate)}
                  onChange={(d) => setStartDate(normalizeDate10(d) ?? '')}
                  disabled={updateProject.isPending}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ flex: '1 1 220px', minWidth: 220 }}>
                <div style={{ fontSize: 12, color: '#666' }}>End date</div>
                <DatePicker
                  oneTap
                  cleanable
                  format="MM/dd/yyyy"
                  value={isoDateToDate(endDate)}
                  onChange={(d) => setEndDate(normalizeDate10(d) ?? '')}
                  disabled={updateProject.isPending}
                  style={{ width: '100%' }}
                />
              </div>
            </Stack>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: '#666' }}>Budget</div>
                <Input
                  value={budgetTarget}
                  onChange={(v) => {
                    const digits = digitsOnly(v);
                    if (!digits) {
                      setBudgetTarget('');
                      return;
                    }
                    const n = Number(digits);
                    setBudgetTarget(Number.isFinite(n) ? formatCurrency(n) : '');
                  }}
                  placeholder="$250,000"
                  disabled={updateProject.isPending}
                />
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Leave blank to clear.</div>
              </div>

              <div>
                <div style={{ fontSize: 12, color: '#666' }}>Contract Number</div>
                <Input
                  value={contractNumber}
                  onChange={setContractNumber}
                  placeholder="e.g. CN-2026-001"
                  disabled={updateProject.isPending}
                />
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Leave blank to clear.</div>
              </div>
            </div>

            {updateProject.error ? (
              <Message type="error" showIcon>
                Failed to save: {String(updateProject.error?.message || updateProject.error)}
              </Message>
            ) : null}

            {deleteProject.error ? (
              <Message type="error" showIcon>
                Failed to delete: {String(deleteProject.error?.message || deleteProject.error)}
              </Message>
            ) : null}

            <Stack justifyContent="space-between" alignItems="center" wrap>
              <Button
                appearance="ghost"
                color="red"
                disabled={!canDelete}
                loading={deleteProject.isPending}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete project
              </Button>

              <Stack spacing={10}>
                <Button appearance="primary" onClick={onSave} disabled={!canSave} loading={updateProject.isPending}>
                  Save
                </Button>
              </Stack>
            </Stack>
          </div>

          <Modal open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} size="sm">
            <Modal.Header>
              <Modal.Title>Delete project?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Message type="warning" showIcon>
                <strong>{project?.name ?? `Project ${projectId}`}</strong> will be sent to Project Trash and retained for
                30 days before permanent deletion.
              </Message>
              <div style={{ marginTop: 10, color: '#666', fontSize: 13 }}>
                You can restore it from Settings → Configuration → Project Trash during that period.
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                appearance="primary"
                color="red"
                onClick={async () => {
                  try {
                    await onDelete();
                  } finally {
                    setConfirmDeleteOpen(false);
                  }
                }}
                loading={deleteProject.isPending}
                disabled={!canDelete}
              >
                Delete
              </Button>
              <Button appearance="subtle" onClick={() => setConfirmDeleteOpen(false)} disabled={deleteProject.isPending}>
                Cancel
              </Button>
            </Modal.Footer>
          </Modal>
        </Panel>
      ) : null}

      {project ? (
        <Panel
          bordered
          collapsible
          defaultExpanded={false}
          className="ppc-project-edit-collapsible"
          header={<div style={sectionHeaderStyle}>People</div>}
          style={{ width: '100%', maxWidth: 1100, margin: '12px auto 0' }}
        >
          {projectUsersError ? (
            <Message type="error" showIcon>
              Failed to load project people.
            </Message>
          ) : null}

          <div style={{ display: 'grid', gap: 10 }}>
            <div className="ppc-people-grid">
              {projectUsersLoading ? (
                <div style={{ color: '#666', fontSize: 12 }}>Loading people…</div>
              ) : projectPeopleDisplay.length === 0 ? (
                <Message type="info" showIcon>
                  No one is assigned to this project yet.
                </Message>
              ) : (
                projectPeopleDisplay.map((p) => (
                  <div key={p.id} className="ppc-person-card">
                    <span
                      className="ppc-person-avatar"
                      style={(() => {
                        const s = avatarStyleForId(p.id);
                        return { background: s.background, borderColor: s.borderColor };
                      })()}
                    >
                      {initialsForName(p.name)}
                    </span>
                    <div style={{ flex: '1 1 auto', minWidth: 0, textAlign: 'center' }}>
                      <div style={{ fontWeight: 800, lineHeight: '18px' }}>{p.name}</div>
                    </div>
                    <div style={{ marginLeft: 'auto' }}>
                      <Button
                        appearance="subtle"
                        color="red"
                        size="sm"
                        onClick={() => setRemovePerson({ id: p.id, name: p.name })}
                        disabled={setProjectUsers.isPending || projectUsersLoading}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <i className="bi bi-dash-circle" aria-hidden="true" />
                          Remove
                        </span>
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Add person</div>
              {usersError ? (
                <Message type="error" showIcon>
                  Failed to load users.
                </Message>
              ) : null}

              <SelectPicker
                ref={addPersonPickerRef}
                searchable
                cleanable
                placeholder={
                  usersLoading
                    ? 'Loading users…'
                    : availableUserOptions.length === 0
                      ? 'All users already assigned'
                      : 'Select a user to add'
                }
                style={{ width: '100%' }}
                popupStyle={{ width: 280, maxWidth: 'calc(100vw - 24px)' }}
                data={availableUserOptionsWithUtil}
                renderOption={(label, item) => {
                  const util = String(item?.utilizationLabel || '0% Average utilization over project duration');
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                      <span style={{ fontSize: 12, color: '#666', flexShrink: 0 }}>{util}</span>
                    </div>
                  );
                }}
                value={Number.isInteger(Number(addPersonUserId)) ? Number(addPersonUserId) : null}
                onChange={async (v) => {
                  const id = Number(v);
                  if (!Number.isInteger(id)) {
                    setAddPersonUserId(null);
                    return;
                  }

                  setAddPersonUserId(id);
                  clearRsuitePickerSearchInputSoon(addPersonPickerRef);
                  try {
                    await addPerson(id);
                  } finally {
                    setAddPersonUserId(null);
                    clearRsuitePickerSearchInputSoon(addPersonPickerRef);
                  }
                }}
                onClean={() => {
                  setAddPersonUserId(null);
                  clearRsuitePickerSearchInputSoon(addPersonPickerRef);
                }}
                onSelect={() => {
                  clearRsuitePickerSearchInputSoon(addPersonPickerRef);
                }}
                disabled={usersLoading || availableUserOptionsWithUtil.length === 0 || setProjectUsers.isPending}
              />
              <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>
                Assigned people appear in Activity Lead/Support dropdowns.
              </div>
              {setProjectUsers.error ? (
                <Message type="error" showIcon style={{ marginTop: 10 }}>
                  Failed to save: {String(setProjectUsers.error?.message || setProjectUsers.error)}
                </Message>
              ) : null}
            </div>
          </div>

          <Modal open={Boolean(removePerson)} onClose={() => setRemovePerson(null)} size="sm">
            <Modal.Header>
              <Modal.Title>Remove person?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Message type="warning" showIcon>
                This will remove <strong>{removePerson?.name ?? 'this person'}</strong> from this project.
              </Message>
              <div style={{ marginTop: 10, color: '#666', fontSize: 13 }}>
                Any projected hours and project rates for this project will also be removed.
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                appearance="primary"
                color="red"
                loading={setProjectUsers.isPending}
                disabled={!removePerson || setProjectUsers.isPending}
                onClick={async () => {
                  const id = Number(removePerson?.id);
                  if (!Number.isInteger(id)) return;
                  try {
                    await removePersonFromProject(id);
                  } finally {
                    setRemovePerson(null);
                  }
                }}
              >
                Remove
              </Button>
              <Button appearance="subtle" onClick={() => setRemovePerson(null)} disabled={setProjectUsers.isPending}>
                Cancel
              </Button>
            </Modal.Footer>
          </Modal>
        </Panel>
      ) : null}

      {project ? (
        <Panel
          bordered
          collapsible
          defaultExpanded={false}
          className="ppc-project-edit-collapsible"
          header={<div style={sectionHeaderStyle}>{hasOnlyProjectTaskOrder ? 'Project' : 'Task Orders'}</div>}
          style={{ width: '100%', maxWidth: 1100, margin: '12px auto 0' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
            <Button appearance="primary" size="sm" onClick={openAddTaskOrder}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                Add task order
                <i className="bi bi-plus" aria-hidden="true" />
              </span>
            </Button>
          </div>

          <Divider style={{ marginTop: 10, marginBottom: 12 }} />

          {taskOrdersLoading ? <div>Loading task orders…</div> : null}
          {taskOrdersError ? (
            <Message type="error" showIcon>
              Failed to load task orders.
            </Message>
          ) : null}

          {!hasExplicitTaskOrders ? (
            <Message type="info" showIcon>
              No explicit task orders for this project.
            </Message>
          ) : null}

          <div style={{ display: 'grid', gap: 10 }}>
            {(hasExplicitTaskOrders ? explicitTaskOrders : defaultTaskOrder ? [defaultTaskOrder] : []).map((o) => {
              const id = Number(o?.id);
              const name = o?.name || '—';
              const start = normalizeDate10(o?.startDate ?? o?.start_date);
              const end = normalizeDate10(o?.endDate ?? o?.end_date);
              const dates = start && end ? `${start} → ${end}` : 'Dates not set';
              const budget =
                o?.budgetTarget === null || o?.budgetTarget === undefined ? null : formatCurrency(o.budgetTarget);

              return (
                <div
                  key={String(id)}
                  style={{
                    border: '1px solid #e9ecef',
                    borderRadius: 10,
                    padding: '10px 12px',
                    background: '#fbfbfc',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, lineHeight: '18px' }}>{name}</div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                      {dates}
                      {budget ? ` • Budget ${budget}` : ''}
                      {o?.isDefault ? ' • Default' : ''}
                    </div>
                  </div>

                  <Button
                    appearance="ghost"
                    size="sm"
                    onClick={() => openEditTaskOrder(o)}
                    disabled={!Number.isInteger(id) || Boolean(o?.isDefault)}
                  >
                    Edit
                  </Button>
                </div>
              );
            })}
          </div>

          <Modal
            open={editTaskOrderOpen}
            onClose={() => setEditTaskOrderOpen(false)}
            size="sm"
          >
            <Modal.Header>
              <Modal.Title>Edit Task Order</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Name</div>
                  <Input value={taskOrderName} onChange={setTaskOrderName} placeholder="e.g. Task Order 001" />
                </div>

                <Stack spacing={12} wrap>
                  <div style={{ flex: '1 1 220px', minWidth: 220 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>Start date</div>
                    <DatePicker
                      oneTap
                      cleanable
                      format="MM/dd/yyyy"
                      value={isoDateToDate(taskOrderStartDate)}
                      onChange={(d) => setTaskOrderStartDate(normalizeDate10(d) ?? '')}
                      style={{ width: '100%' }}
                    />
                    {projectStartIso ? (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Project start: {projectStartIso}</div>
                    ) : null}
                  </div>
                  <div style={{ flex: '1 1 220px', minWidth: 220 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>End date</div>
                    <DatePicker
                      oneTap
                      cleanable
                      format="MM/dd/yyyy"
                      value={isoDateToDate(taskOrderEndDate)}
                      onChange={(d) => setTaskOrderEndDate(normalizeDate10(d) ?? '')}
                      style={{ width: '100%' }}
                    />
                    {projectEndIso ? (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Project end: {projectEndIso}</div>
                    ) : null}
                  </div>
                </Stack>

                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Budget</div>
                  <Input
                    value={taskOrderBudgetTarget}
                    onChange={(v) => {
                      const digits = digitsOnly(v);
                      if (!digits) {
                        setTaskOrderBudgetTarget('');
                        return;
                      }
                      const n = Number(digits);
                      setTaskOrderBudgetTarget(Number.isFinite(n) ? formatCurrency(n) : '');
                    }}
                    placeholder="$50,000"
                    disabled={updateTaskOrder.isPending}
                  />
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Leave blank to clear.</div>
                </div>

                {updateTaskOrder.error ? (
                  <Message type="error" showIcon>
                    Failed to save: {String(updateTaskOrder.error?.message || updateTaskOrder.error)}
                  </Message>
                ) : null}

                {!canSaveTaskOrder ? (
                  <Message type="warning" showIcon>
                    Please provide a name, valid dates (within project range), and ensure start ≤ end.
                  </Message>
                ) : null}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                appearance="primary"
                onClick={() => void saveTaskOrderEdit()}
                loading={updateTaskOrder.isPending}
                disabled={!canSaveTaskOrder}
              >
                Save
              </Button>
              <Button appearance="ghost" onClick={() => setEditTaskOrderOpen(false)} disabled={updateTaskOrder.isPending}>
                Cancel
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal open={addTaskOrderOpen} onClose={() => setAddTaskOrderOpen(false)} size="sm">
            <Modal.Header>
              <Modal.Title>Add Task Order</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Name</div>
                  <Input value={newTaskOrderName} onChange={setNewTaskOrderName} placeholder="e.g. Task Order 001" />
                </div>

                <Stack spacing={12} wrap>
                  <div style={{ flex: '1 1 220px', minWidth: 220 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>Start date</div>
                    <DatePicker
                      oneTap
                      cleanable
                      format="MM/dd/yyyy"
                      value={isoDateToDate(newTaskOrderStartDate)}
                      onChange={(d) => setNewTaskOrderStartDate(normalizeDate10(d) ?? '')}
                      style={{ width: '100%' }}
                    />
                    {projectStartIso ? (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Project start: {projectStartIso}</div>
                    ) : null}
                  </div>
                  <div style={{ flex: '1 1 220px', minWidth: 220 }}>
                    <div style={{ fontSize: 12, color: '#666' }}>End date</div>
                    <DatePicker
                      oneTap
                      cleanable
                      format="MM/dd/yyyy"
                      value={isoDateToDate(newTaskOrderEndDate)}
                      onChange={(d) => setNewTaskOrderEndDate(normalizeDate10(d) ?? '')}
                      style={{ width: '100%' }}
                    />
                    {projectEndIso ? (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Project end: {projectEndIso}</div>
                    ) : null}
                  </div>
                </Stack>

                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Budget</div>
                  <Input
                    value={newTaskOrderBudgetTarget}
                    onChange={(v) => {
                      const digits = digitsOnly(v);
                      if (!digits) {
                        setNewTaskOrderBudgetTarget('');
                        return;
                      }
                      const n = Number(digits);
                      setNewTaskOrderBudgetTarget(Number.isFinite(n) ? formatCurrency(n) : '');
                    }}
                    placeholder="$50,000"
                    disabled={createTaskOrder.isPending}
                  />
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Leave blank for no budget.</div>
                </div>

                {createTaskOrder.error ? (
                  <Message type="error" showIcon>
                    Failed to create: {String(createTaskOrder.error?.message || createTaskOrder.error)}
                  </Message>
                ) : null}

                {!canCreateTaskOrder ? (
                  <Message type="warning" showIcon>
                    Please provide a name and valid dates (within project range), and ensure start ≤ end.
                  </Message>
                ) : null}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                appearance="primary"
                onClick={() => void createNewTaskOrder()}
                loading={createTaskOrder.isPending}
                disabled={!canCreateTaskOrder}
              >
                Create
              </Button>
              <Button appearance="ghost" onClick={() => setAddTaskOrderOpen(false)} disabled={createTaskOrder.isPending}>
                Cancel
              </Button>
            </Modal.Footer>
          </Modal>
        </Panel>
      ) : null}

      {project ? (
        <Panel
          bordered
          collapsible
          defaultExpanded={false}
          className="ppc-project-edit-collapsible"
          header={<div style={sectionHeaderStyle}>Milestones</div>}
          style={{ width: '100%', maxWidth: 1100, margin: '12px auto 0' }}
        >
          {milestonesError ? (
            <Message type="error" showIcon>
              Failed to load milestones.
            </Message>
          ) : null}

          {milestonesLoading ? <div>Loading milestones…</div> : null}

          {!milestonesLoading && milestones.length === 0 ? (
            <Message type="info" showIcon>
              No milestones yet.
            </Message>
          ) : null}

          <div style={{ display: 'grid', gap: 8 }}>
            {milestones.map((m) => (
              <div key={`${m?.project_id}-${m?.id}`} className="ppc-milestone-row">
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {String(m?.name || 'Milestone')}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>{String(m?.milestone_date || '—')}</div>
                </div>

                <div className="ppc-milestone-row__actions">
                  {m?.is_auto ? (
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>This milestone was automatically generated from task order dates</Tooltip>}
                    >
                      <Tag color="blue" size="sm">Auto</Tag>
                    </Whisper>
                  ) : null}

                  {!m?.is_auto ? (
                    <Button size="xs" appearance="subtle" onClick={() => openEditMilestoneModal(m)}>
                      Edit
                    </Button>
                  ) : null}

                  <Button
                    size="xs"
                    appearance="subtle"
                    color="red"
                    onClick={() => {
                      const mid = Number(m?.id);
                      const milestoneId = Number.isInteger(mid) ? mid : String(m?.id || '').trim();
                      if (!milestoneId) return;
                      setPendingDeleteMilestone({ id: milestoneId, name: String(m?.name || 'Milestone') });
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="ppc-milestone-add-wrap">
            <Button appearance="primary" size="sm" onClick={openCreateMilestoneModal}>
              Add milestone
            </Button>
          </div>

          <Modal open={milestoneModalOpen} onClose={closeMilestoneModal} size="xs">
            <Modal.Header>
              <Modal.Title>{milestoneModalMode === 'edit' ? 'Edit milestone' : 'Add milestone'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Name</div>
                  <Input value={milestoneNameInput} onChange={setMilestoneNameInput} placeholder="Milestone name" />
                </div>

                <div>
                  <div style={{ fontSize: 12, color: '#666' }}>Date</div>
                  <DatePicker
                    oneTap
                    cleanable={false}
                    format="MM/dd/yyyy"
                    value={isoDateToDate(normalizeDate10(milestoneDateInput))}
                    onChange={(d) => setMilestoneDateInput(normalizeDate10(d) ?? '')}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                appearance="primary"
                onClick={saveMilestone}
                loading={createMilestone.isPending || updateMilestone.isPending}
                disabled={!milestoneFormValid}
              >
                Save
              </Button>
              <Button appearance="subtle" onClick={closeMilestoneModal}>
                Cancel
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal
            open={Boolean(pendingDeleteMilestone)}
            onClose={() => {
              if (deleteMilestone.isPending) return;
              setPendingDeleteMilestone(null);
            }}
            size="xs"
          >
            <Modal.Header>
              <Modal.Title>Delete milestone?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Message type="warning" showIcon>
                This will permanently delete <strong>{pendingDeleteMilestone?.name || 'this milestone'}</strong>.
              </Message>
            </Modal.Body>
            <Modal.Footer>
              <Button
                appearance="primary"
                color="red"
                loading={deleteMilestone.isPending}
                disabled={deleteMilestone.isPending}
                onClick={() => void confirmDeleteMilestone()}
              >
                Delete
              </Button>
              <Button
                appearance="subtle"
                disabled={deleteMilestone.isPending}
                onClick={() => setPendingDeleteMilestone(null)}
              >
                Cancel
              </Button>
            </Modal.Footer>
          </Modal>
        </Panel>
      ) : null}
    </div>
  );
}
